import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { 
  MapPin, Phone, Mail, Clock, Send, CheckCircle2, Loader2, 
  MessageSquare, Briefcase, ChevronRight, Award, Users, Sparkles, GraduationCap 
} from 'lucide-react';
import { db } from '../Firebase';
import { collection, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';

export default function Contact() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab selector state based on query params
  const activeTab = searchParams.get('tab') === 'careers' ? 'careers' : 'contact';

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  // Contact Form States
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    hospitalName: '',
    designation: '',
    requirement: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Careers States
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  const perks = [
    {
      icon: Award,
      title: 'Industry Leadership',
      desc: 'Work with the leading distributor representing 40+ multinational healthcare brands across Maharashtra and Karnataka.'
    },
    {
      icon: Users,
      title: 'Collaborative Culture',
      desc: 'Collaborate daily with cardiac specialists, hospital planners, and top pharmacy operators in a high-trust workspace.'
    },
    {
      icon: Sparkles,
      title: 'Innovative Projects',
      desc: 'Be a part of planning high-end class 100 modular Operation Theatres and complex hospital gas pipe grids.'
    },
    {
      icon: GraduationCap,
      title: 'Career Advancement',
      desc: 'Access ongoing training, certifications in biomedical technologies, and direct mentorship from our industry veterans.'
    }
  ];

  useEffect(() => {
    if (activeTab !== 'careers') return;
    
    const jobsRef = collection(db, 'jobs');

    const unsubscribe = onSnapshot(jobsRef, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const unique = [];
      const seen = new Set();
      for (const j of list) {
        if (j.title) {
          const key = `${j.title.trim().toLowerCase()}_${(j.location || '').trim().toLowerCase()}_${(j.type || '').trim().toLowerCase()}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(j);
          }
        }
      }
      setJobs(unique);
      setJobsLoading(false);
    }, (err) => {
      console.error("Error loading jobs:", err);
      setJobsLoading(false);
    });

    return () => unsubscribe();
  }, [activeTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Allow only numbers, max length 10
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phone: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.hospitalName || !formData.requirement) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Please fill in all required fields (*) including Area of Interest.',
        icon: 'warning',
        confirmButtonColor: '#DE2929'
      });
      return;
    }

    // Phone validation: exactly 10 digits
    if (formData.phone.length !== 10) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Phone number must be exactly 10 digits.',
        icon: 'warning',
        confirmButtonColor: '#DE2929'
      });
      return;
    }

    // Email validation: if provided, must be valid
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        Swal.fire({
          title: 'Invalid Email Address',
          text: 'Please enter a valid email address.',
          icon: 'warning',
          confirmButtonColor: '#DE2929'
        });
        return;
      }
    }

    setLoading(true);

    try {
      await addDoc(collection(db, 'consultations'), {
        ...formData,
        submittedAt: serverTimestamp(),
        status: 'new'
      });

      // Send to FormSubmit
      try {
        await fetch("https://formsubmit.co/ajax/kirandistributors.miraj@gmail.com", {
          method: "POST",
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            _subject: "New Consultation Request - " + formData.name,
            _template: "table",
            "Full Name": formData.name,
            "Phone Number": formData.phone,
            "Email Address": formData.email || "Not Provided",
            "Hospital / Clinic": formData.hospitalName,
            "Designation": formData.designation || "Not Provided",
            "Area of Interest": formData.requirement,
            "Project Scope / Message": formData.message || "No message"
          })
        });
      } catch (mailErr) {
        console.error("Error sending to FormSubmit:", mailErr);
      }
      
      Swal.fire({
        title: 'Request Submitted!',
        text: 'Your consultation request has been logged successfully.',
        icon: 'success',
        confirmButtonColor: '#DE2929'
      });
      
      setSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        hospitalName: '',
        designation: '',
        requirement: '',
        message: ''
      });
    } catch (err) {
      console.error("Error adding consultation doc: ", err);
      Swal.fire({
        title: 'Submission Failed',
        text: 'Failed to submit request. Please try again or call us directly.',
        icon: 'error',
        confirmButtonColor: '#DE2929'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = (jobTitle) => {
    navigate('/apply-job', { state: { position: jobTitle } });
  };

  return (
    <div className="font-sans bg-brand-light min-h-screen">
      
      {/* Page Header */}
      <section className="bg-brand-navy text-white pt-28 pb-16 relative overflow-hidden grid-mesh">
        <div className="absolute inset-0 bg-brand-navy/80 z-10" />
        <div className="absolute inset-0 z-0 bg-brand-navy/40" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-20 text-center space-y-4">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-green font-poppins block">
            {activeTab === 'contact' ? 'Get In Touch' : 'Careers'}
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-montserrat text-white tracking-tight">
            {activeTab === 'contact' ? 'Connect With Our Planners' : 'Build Your Career with Kiran'}
          </h1>
          <p className="text-sm text-brand-silver max-w-2xl mx-auto font-poppins">
            {activeTab === 'contact' 
              ? 'Coordinate with our pharmaceutical distribution desk or modular Operation Theatre setup engineers today.'
              : 'Join a legacy of reliability and innovation. We are looking for passionate, driven professionals to help us shape the future of healthcare infrastructure.'
            }
          </p>

          {/* Toggler on Top of Page */}
          <div className="flex justify-center pt-6 relative z-30">
            <div className="bg-brand-navy/60 backdrop-blur-md p-1.5 rounded-full border border-white/10 flex items-center shadow-2xl">
              <button
                type="button"
                onClick={() => handleTabChange('contact')}
                className={`px-6 py-2.5 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all duration-300 font-poppins flex items-center gap-2 ${
                  activeTab === 'contact'
                    ? 'bg-brand-blue text-white shadow-lg'
                    : 'text-brand-silver hover:text-white'
                }`}
              >
                <Phone size={14} />
                Contact Us
              </button>
              <button
                type="button"
                onClick={() => handleTabChange('careers')}
                className={`px-6 py-2.5 rounded-full text-xs sm:text-sm font-bold tracking-wide transition-all duration-300 font-poppins flex items-center gap-2 ${
                  activeTab === 'careers'
                    ? 'bg-brand-blue text-white shadow-lg'
                    : 'text-brand-silver hover:text-white'
                }`}
              >
                <Briefcase size={14} />
                Careers
              </button>
            </div>
          </div>
        </div>
      </section>

      {activeTab === 'contact' ? (
        <>
          {/* Main Contact Content */}
          <section className="py-16 max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              
              {/* Left Column: Office Coordinates & Actions */}
              <div className="lg:col-span-5 space-y-8">
                
                <div className="border-l-4 border-brand-blue pl-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-blue font-poppins block mb-1">Corporate Details</span>
                  <h2 className="text-2xl font-bold font-montserrat text-brand-navy">Registered Offices</h2>
                </div>

                <div className="space-y-6">
                  
                  {/* Head Office Coordinates */}
                  <div className="bg-white p-8 rounded-3xl border border-brand-silver/50 shadow-md space-y-4 hover:-translate-y-1 hover:shadow-lg hover:border-brand-blue/30 transition-all duration-300 group cursor-pointer">
                    <div className="flex gap-4 items-start">
                      <div className="w-12 h-12 rounded-2xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                        <MapPin size={24} />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-sm sm:text-base text-brand-navy font-montserrat uppercase tracking-wider">Head Office (Miraj)</h3>
                        <p className="text-xs sm:text-sm text-gray-600 font-poppins mt-2 leading-relaxed">
                          Kiran Distributors, Kiran Building,<br />
                          Gandhi Chowk, Miraj – 416410<br />
                          Sangli District, Maharashtra.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Branch Office Coordinates */}
                  <div className="bg-white p-8 rounded-3xl border border-brand-silver/50 shadow-md space-y-4 hover:-translate-y-1 hover:shadow-lg hover:border-brand-green/30 transition-all duration-300 group cursor-pointer">
                    <div className="flex gap-4 items-start">
                      <div className="w-12 h-12 rounded-2xl bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300">
                        <MapPin size={24} />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-sm sm:text-base text-brand-navy font-montserrat uppercase tracking-wider">Branch Office (Sangli)</h3>
                        <p className="text-xs sm:text-sm text-gray-600 font-poppins mt-2 leading-relaxed">
                          Kiran Distributors, Shriram Akshay Building,<br />
                          G4, Bapat Mala Road, Sangli - 416416<br />
                          Maharashtra.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Logistics & Call Centers */}
                  <div className="bg-white p-8 rounded-3xl border border-brand-silver/50 shadow-md space-y-5 hover:-translate-y-1 hover:shadow-lg hover:border-brand-blue/30 transition-all duration-300 group">
                    <h4 className="text-xs sm:text-sm font-bold font-montserrat uppercase tracking-wider text-brand-navy border-b border-brand-silver pb-3 group-hover:text-brand-blue transition-colors duration-300">Logistics Coordination</h4>
                    <div className="space-y-4 text-xs sm:text-sm font-poppins text-gray-600">
                      <div className="flex items-center gap-3">
                        <Phone size={18} className="text-brand-blue group-hover:scale-110 transition-transform duration-300 shrink-0" />
                        <span>Mobile: <a href="tel:9322089040" className="font-semibold text-brand-navy hover:text-brand-blue">9322089040</a></span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail size={18} className="text-brand-blue group-hover:scale-110 transition-transform duration-300 shrink-0" />
                        <span>Email: <a href="mailto:kirandistributors.miraj@gmail.com" className="font-semibold text-brand-navy hover:text-brand-blue">kirandistributors.miraj@gmail.com</a></span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock size={18} className="text-brand-green group-hover:scale-110 transition-transform duration-300 shrink-0" />
                        <span>Working Hours: <strong>10:00 AM – 7:00 PM</strong> (Mon - Sat)</span>
                      </div>
                    </div>
                  </div>

                  {/* Direct Instant Action Buttons */}
                  <div className="grid grid-cols-2 gap-4">
                    <a
                      href="https://wa.me/919225836006"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-green py-4 justify-center text-sm font-bold font-poppins"
                    >
                      <MessageSquare size={18} />
                      WhatsApp Direct
                    </a>
                    <a
                      href="mailto:kirandistributors.miraj@gmail.com"
                      className="btn-secondary py-4 justify-center text-sm font-bold font-poppins"
                    >
                      <Mail size={18} />
                      Email Office
                    </a>
                  </div>

                </div>
              </div>

              {/* Right Column: Contact & Inquiry Form */}
              <div className="lg:col-span-7 bg-white p-8 sm:p-10 rounded-3xl border border-brand-silver/50 shadow-md">
                
                <div className="border-l-4 border-brand-blue pl-4 mb-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-blue font-poppins block mb-1">Inquiry Form</span>
                  <h2 className="text-xl sm:text-2xl font-bold font-montserrat text-brand-navy">Request Consultation</h2>
                </div>

                {success ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12 space-y-4"
                  >
                    <div className="w-16 h-16 bg-brand-green/10 text-brand-green rounded-full flex items-center justify-center mx-auto mb-2 animate-bounce">
                      <CheckCircle2 size={36} />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy font-montserrat">Consultation Requested!</h3>
                    <p className="text-sm text-gray-500 font-poppins max-w-sm mx-auto">
                      Thank you. Your consultation request has been logged. A healthcare planning expert from Kiran Distributors will contact you within 24 business hours.
                    </p>
                    <button 
                      onClick={() => setSuccess(false)}
                      className="btn-primary mx-auto"
                    >
                      Send Another Request
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* Full Name & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          required
                          placeholder="Enter your name"
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs text-brand-navy"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="92258xxxxx"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs text-brand-navy"
                        />
                      </div>
                    </div>

                    {/* Hospital/Clinic & Designation */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Hospital / Clinic *</label>
                        <input
                          type="text"
                          name="hospitalName"
                          required
                          placeholder="Enter hospital / clinic name"
                          value={formData.hospitalName}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs text-brand-navy"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Designation</label>
                        <input
                          type="text"
                          name="designation"
                          placeholder="MD / Administrator"
                          value={formData.designation}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs text-brand-navy"
                        />
                      </div>
                    </div>

                    {/* Email Address & Area of Interest */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="doctor@hospital.com"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs text-brand-navy"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Area of Interest *</label>
                        <select
                          name="requirement"
                          required
                          value={formData.requirement}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs text-brand-navy"
                        >
                          <option value="">Select Area of Interest *</option>
                          <option value="Modular OT Setup">Modular Operation Theatre (OT) Setup</option>
                          <option value="ICU Setup Solutions">ICU Setup & Integration</option>
                          <option value="Hospital Furniture">Hospital Furniture & Fixtures</option>
                          <option value="Medical Equipment Supply">Medical Equipment Supply</option>
                          <option value="Pharmaceutical Distribution">Pharmaceutical & Surgical Distribution</option>
                          <option value="Entire Setup Planning">Complete Turnkey Hospital Setup</option>
                          <option value="General Enquiry">General Enquiry</option>
                        </select>
                      </div>
                    </div>

                    {/* Project Scope */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5 font-poppins">Project Scope / Custom Needs</label>
                      <textarea
                        name="message"
                        rows="5"
                        placeholder="Briefly describe your expansion plans or equipment requirements..."
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue hover:border-brand-blue/50 transition-colors duration-200 font-poppins text-xs resize-none text-brand-navy"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full btn-primary justify-center font-poppins py-4 text-xs sm:text-sm font-bold shadow-lg shadow-brand-blue/10"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin" size={20} />
                          Submitting Request...
                        </>
                      ) : (
                        <>
                          <Send size={16} className="mr-1" />
                          Request Consultation Call
                        </>
                      )}
                    </button>
                  </form>
                )}

              </div>
            </div>
          </section>

          {/* Embedded Maps Section (Miraj Head Office detailed) */}
          <section className="pt-12 pb-0 bg-brand-light border-t border-brand-silver/50">
            <div className="w-full">
              <div className="text-center max-w-2xl mx-auto mb-10 space-y-2 px-6">
                <h3 className="font-extrabold text-2xl text-brand-navy font-montserrat">Find Us On Google Maps</h3>
              </div>
              <div className="w-full h-[500px] overflow-hidden shadow-inner border-t border-brand-silver bg-slate-900">
                <iframe
                  title="Kiran Distributors Miraj Head Office Google Maps Locator"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d907.4907605600057!2d74.64513132039558!3d16.830323266805436!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc1234ffa5ffe4f%3A0x8d60b3238c2a3e00!2sKiran%20Distributors!5e0!3m2!1sen!2sin!4v1780231355831!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Why Join Us Perks Section */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-blue font-poppins block">Core Culture</span>
                <h2 className="text-3xl font-bold font-montserrat text-brand-navy">Why Join Kiran Distributors?</h2>
                <p className="text-sm text-gray-500 font-poppins">We foster an environment of growth, continuous training, and high ethical compliance standards.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {perks.map((perk, idx) => (
                  <div 
                    key={idx} 
                    className="p-8 rounded-3xl border border-brand-silver bg-white shadow-sm hover:border-brand-blue hover:shadow-xl hover:shadow-brand-blue/5 hover:-translate-y-2 transition-all duration-300 space-y-4 flex flex-col justify-between group cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-brand-blue/10 text-brand-blue flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300">
                      <perk.icon size={24} />
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-extrabold text-lg text-brand-navy font-montserrat tracking-tight group-hover:text-brand-blue transition-colors">{perk.title}</h4>
                      <p className="text-sm text-gray-600 font-poppins leading-relaxed">{perk.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Job Board Layout - Centered Stack */}
          <section className="py-20 max-w-4xl mx-auto px-6">
            <div className="text-center mb-12 space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-blue font-poppins block">Current Opportunities</span>
              <h2 className="text-3xl font-extrabold font-montserrat text-brand-navy">Explore Open Roles</h2>
              <p className="text-sm text-gray-500 font-poppins max-w-xl mx-auto">Select a position below to view coordinates, requirements, and apply directly.</p>
            </div>

            <div className="space-y-8">
              {jobsLoading ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4 bg-white rounded-3xl border border-brand-silver/50 shadow-md">
                  <Loader2 className="animate-spin text-brand-blue" size={40} />
                  <p className="text-sm font-semibold font-poppins">Loading current opportunities...</p>
                </div>
              ) : jobs.length === 0 ? (
                <div className="bg-white p-12 text-center rounded-3xl border border-brand-silver/50 shadow-md text-gray-400">
                  <Briefcase size={40} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-xs font-semibold">No open roles found. Try submitting a general application below.</p>
                </div>
              ) : (
                jobs.map((job, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white p-8 sm:p-10 rounded-3xl border border-brand-silver/80 shadow-md space-y-6 hover:shadow-xl hover:border-brand-blue/30 transition-all duration-300 group"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 border-b border-brand-silver pb-4">
                      <div>
                        <span className="text-[10px] font-bold text-brand-blue uppercase tracking-widest bg-brand-blue/10 px-2.5 py-1 rounded font-poppins inline-block mb-1">
                          {job.department}
                        </span>
                        <h3 className="font-extrabold text-xl sm:text-2xl text-brand-navy font-montserrat tracking-tight">{job.title}</h3>
                      </div>
                      <span className="bg-brand-light px-3 py-1 rounded-lg border border-brand-silver text-xs font-bold font-poppins text-gray-600">
                        {job.type}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-poppins text-gray-600">
                      <div className="flex items-center gap-2.5">
                        <MapPin size={18} className="text-brand-blue shrink-0" />
                        <span>Location: <strong>{job.location}</strong></span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Briefcase size={18} className="text-brand-green shrink-0" />
                        <span>Experience: <strong>{job.experience}</strong></span>
                      </div>
                    </div>

                    <p className="text-sm sm:text-base text-gray-600 font-poppins leading-relaxed">
                      {job.desc}
                    </p>

                    <div className="space-y-3 bg-brand-light/50 p-6 rounded-2xl border border-brand-silver/50">
                      <h4 className="text-xs font-bold font-montserrat uppercase tracking-wider text-brand-navy">Key Requirements:</h4>
                      <ul className="space-y-2 text-sm text-gray-500 font-poppins">
                        {job.requirements?.map((req, reqIdx) => (
                          <li key={reqIdx} className="flex gap-2 items-start">
                            <ChevronRight size={16} className="text-brand-blue shrink-0 mt-1" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={() => handleApplyClick(job.title)}
                        className="btn-primary py-3 px-8 text-sm flex items-center gap-2 font-poppins shadow-md shadow-brand-blue/10"
                      >
                        Apply For This Role <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}

              {/* General Application Callout */}
              <div className="bg-brand-navy text-white p-8 sm:p-10 rounded-3xl text-center space-y-6 relative overflow-hidden shadow-lg grid-mesh mt-12">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full pointer-events-none" />
                <div className="relative z-10 max-w-2xl mx-auto space-y-4">
                  <h3 className="text-2xl font-bold font-montserrat text-white">Don't see a perfect fit?</h3>
                  <p className="text-sm text-brand-silver font-poppins leading-relaxed">
                    We are always on the lookout for talented pharmacists, logistics experts, biomedical engineers, and coordinators. Submit a general open application and we will keep you in mind!
                  </p>
                  <button
                    onClick={() => handleApplyClick('Other / General Application')}
                    className="btn-primary py-3 px-8 text-sm inline-flex items-center gap-2 font-poppins mx-auto shadow-md"
                  >
                    Submit General Application <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

    </div>
  );
}
